<template>
  <v-col class="bg-reporting-menu d-flex flex-column h-100">
    <v-row class="my-0">
      <ReportingSelection @update:scenarios="resetData" />
    </v-row>
    <div class="h-100">
      <AgGridVue
        v-show="reportingStore.isSidebarShown && rowData.length"
        tree-data
        animate-rows
        dense
        class="h-100"
        :row-data="rowData"
        :column-defs="columns"
        :grid-options="gridOptions"
        @grid-ready="onGridReady"
        @first-data-rendered="onExpand"
      />
    </div>
    <v-row class="my-0 justify-end">
      <v-btn
        v-show="reportingStore.isSidebarShown"
        data-id-e2e="btnGenerateReports"
        variant="flat"
        primary
        :disabled="!reportingStore.baselineIds.length"
        @click="onGenerateReports"
      >
        {{ t('reportingPage.generateReports') }}
      </v-btn>
    </v-row>
  </v-col>
</template>

<script>
import {
  pick,
  values,
  reject,
  get,
  size,
  reduce,
  map,
  flatMap,
  sortBy,
  uniqBy,
  concat,
  some,
  omit,
  includes,
  each,
} from 'lodash';
import { useRoute, useRouter } from 'vue-router';
import menuLevel from '@enums/reporting-data-level';
import reportingFilters from '@enums/reporting-filters';
import agGridUtils from '@/utils/ag-grid-utils';
import agGridIcon from '@/components/AgGridIcon.vue';

import { useReportingStore } from '@/stores/reporting';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useBundlesStore } from '@/stores/bundles';
import { useContextStore } from '@/stores/context';
import { useWorkpackageScenarioSelectStore } from '@/stores/workpackage-scenario-select';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';

import { useInternationalization } from '@/composables/use-internationalization';
import { useFeature } from '@/composables/use-feature';

export default {
  setup() {
    const reportingStore = useReportingStore();
    const workpackagesStore = useWorkpackagesStore();
    const contextStore = useContextStore();
    const bundlesStore = useBundlesStore();
    const workpackageScenarioSelectStore = useWorkpackageScenarioSelectStore();
    const assortmentCanvasStore = useAssortmentCanvasStore();
    const route = useRoute();
    const router = useRouter();
    const { t, tkey } = useInternationalization('reportingPage');
    const { bundlesEnabled, showLiveCanvasInReportingMenu, enableFinalCheckpoints } = useFeature();

    return {
      reportingStore,
      workpackagesStore,
      contextStore,
      bundlesStore,
      workpackageScenarioSelectStore,
      assortmentCanvasStore,
      route,
      router,
      t,
      tkey,
      bundlesEnabled,
      showLiveCanvasInReportingMenu,
      enableFinalCheckpoints,
    };
  },

  data() {
    return {
      rowData: [],
      columns: [
        {
          headerName: this.tkey('menuHeaders.forecasted'),
          headerClass: 'justify-center',
          field: 'hasBeenForecast',
          cellClass: 'd-flex align-center justify-center',
          cellRenderer: params =>
            agGridUtils.utils.customIconRenderer(params, {
              isDisplayed:
                get(params, 'data.menuLevel') === menuLevel.checkpoint ||
                (get(params, 'data.menuLevel') === menuLevel.canvas &&
                  get(params, 'data.live', false) &&
                  this.showLiveCanvasInReportingMenu),
              icon: get(params, 'data.hasBeenForecast') ? 'check' : 'close',
              classes: get(params, 'data.hasBeenForecast') ? 'forecasted' : 'notForecasted',
            }),
          headerTooltip: this.tkey('menuHeaders.forecasted'),
          resizable: false,
          maxWidth: 40,
        },
        {
          headerName: this.t('general.final'),
          headerClass: 'justify-center',
          field: 'isFinal',
          hide: !this.enableFinalCheckpoints,
          cellClass: 'd-flex align-center justify-center',
          cellRenderer: agGridIcon,
          cellRendererParams: params => ({
            value: params.value ? 'mdi-star' : 'mdi-star-outline',
            color: 'primary',
            size: '20px',
          }),
          headerTooltip: this.t('general.final'),
          resizable: false,
          maxWidth: 50,
          cellClassRules: { hidden: params => get(params, 'data.menuLevel') === menuLevel.bundle },

          valueGetter: params => {
            if (!params.data) {
              return false;
            }

            const { isWorkpackageFinal, isScenarioFinal } = this.assortmentCanvasStore;

            const isFinal = {
              bundle: () => false, // Bundles are never final
              workpackage: () => isWorkpackageFinal(params.data._id),
              scenario: () => isScenarioFinal(params.data._id),
              canvas: () => params.data.isFinalCheckpoint,
              checkpoint: () => params.data.isFinalCheckpoint,
            };

            if (!isFinal[params.data.menuLevel]) {
              return false;
            }
            return isFinal[params.data.menuLevel]();
          },
        },
        {
          headerName: this.tkey('menuHeaders.baseShort'),
          headerClass: 'justify-center',
          cellClass: 'd-flex justify-center align-center',
          cellRendererParams: { field: reportingFilters.baseline },
          cellRenderer: params =>
            get(params, 'data.menuLevel') === menuLevel.checkpoint ||
            (get(params, 'data.menuLevel') === menuLevel.canvas &&
              get(params, 'data.live', false) &&
              this.showLiveCanvasInReportingMenu)
              ? agGridUtils.utils.customCheckboxButtonRenderer(params, {
                  eventType: 'click',
                  handler: this.handleCheckboxChange,
                  isSelected: includes(this.reportingStore.baselineIds, params.data._id),
                })
              : '',
          headerTooltip: this.tkey('menuHeaders.base'),
          resizable: false,
          maxWidth: 40,
        },
        {
          headerName: this.tkey('menuHeaders.comparisonShort'),
          headerClass: 'justify-center',
          cellRenderer: params =>
            get(params, 'data.menuLevel') === menuLevel.checkpoint ||
            (get(params, 'data.menuLevel') === menuLevel.canvas &&
              get(params, 'data.live', false) &&
              this.showLiveCanvasInReportingMenu)
              ? agGridUtils.utils.customCheckboxButtonRenderer(params, {
                  eventType: 'click',
                  handler: this.handleCheckboxChange,
                  isSelected: includes(this.reportingStore.comparisonIds, params.data._id),
                  isComponentEnabled: this.isComparisonEnabled,
                })
              : '',
          cellRendererParams: { field: reportingFilters.comparison },
          cellClass: 'd-flex justify-center align-center',
          headerTooltip: this.tkey('menuHeaders.comparison'),
          resizable: false,
          maxWidth: 40,
        },
        {
          headerName: this.tkey('menuHeaders.comparison2Short'),
          headerClass: 'justify-center',
          cellRenderer: params =>
            get(params, 'data.menuLevel') === menuLevel.checkpoint ||
            (get(params, 'data.menuLevel') === menuLevel.canvas &&
              get(params, 'data.live', false) &&
              this.showLiveCanvasInReportingMenu)
              ? agGridUtils.utils.customCheckboxButtonRenderer(params, {
                  eventType: 'click',
                  handler: this.handleCheckboxChange,
                  isSelected: includes(this.reportingStore.comparison2Ids, params.data._id),
                  isComponentEnabled: this.isComparisonEnabled,
                })
              : '',
          cellRendererParams: { field: reportingFilters.comparison2 },
          cellClass: 'd-flex justify-center align-start',
          headerTooltip: this.tkey('menuHeaders.comparison2'),
          resizable: false,
          maxWidth: 40,
        },
      ],
      gridApi: null,
      gridOptions: {
        columnMenu: 'legacy',
        animateRows: true,
        headerHeight: 30,
        rowHeight: 30,
        suppressContextMenu: true,
        suppressGroupRowsSticky: true,
        statusBar: false,

        defaultColDef: {
          filter: false,
          sortable: false,
          menuTabs: [],
          suppressMovable: true,
          tooltipShowDelay: 0,
          suppressHeaderMenuButton: true, // hide header menu by
          headerClass: 'px-0',
        },

        onRowGroupOpened: params => {
          params.api.redrawRows({ rowNodes: [params.node] });
        },

        autoGroupColumnDef: {
          headerName: '',
          field: 'name',
          cellRendererParams: { suppressCount: true },
          cellClassRules: { 'ag-row-highlighted': this.isRowHighlighted },
          hide: true,
          flex: 1,
          minWidth: 200,
          tooltipShowDelay: 0,
          tooltipValueGetter: params => get(params, 'data.name', ''),
        },

        getDataPath(data) {
          return data.hierarchy;
        },
        getRowId(row) {
          const { data } = row;
          return data._id;
        },
      },
      noBundle: { _id: null, name: this.t('reportingPage.noBundle') },
    };
  },

  async created() {
    // Pre-fetch the list of final workpackages
    await this.assortmentCanvasStore.fetchFinalWorkpackages();
    await this.resetData();
  },

  methods: {
    // build the next querystring based on tree selection & current location path
    getQueryFilters(params = {}) {
      const otherFiltersKeys = reject(values(reportingFilters), key => key === params.field);

      return otherFiltersKeys.reduce((acc, key) => {
        const propertyId = `${key}Ids`;
        return {
          ...acc,
          ...(size(this.reportingStore[propertyId]) && { [key]: this.reportingStore[propertyId] }),
        };
      }, {});
    },

    async handleCheckboxChange(params) {
      /* On click we have to navigate the user to the "new url", update state and the information on which
      rows can be selected or not.
      */
      const { field, api } = params;
      const nextFilters = {
        ...this.getQueryFilters(params),
        ...this.getNextFilters(params),
        metricValue: this.reportingStore.metricValue,
      };

      // set previous report data as stale
      this.reportingStore.setReportDataStale(true);

      // when the last baseline is unselected, also unselect comparison
      if (field === reportingFilters.baseline && !size(nextFilters[field])) {
        nextFilters.comparison = [];
        nextFilters.comparison2 = [];
      }
      // when the last comparison is unselected, also unselect comparison2
      if (field === reportingFilters.comparison && !size(nextFilters[field])) {
        nextFilters.comparison2 = [];
      }

      this.reportingStore.setSelection({
        field: 'attributeValue',
        value: nextFilters.attributeValue,
      });
      this.reportingStore.resetFilters(nextFilters);
      this.reportingStore.resetReportingFilters();
      api.redrawRows([]); // redraw all rows so the disabled/enabled flag is updated
      this.navigateToNewUrl(nextFilters);

      // after state is populated, we can get the values for all attributes in all scenarios
      await this.reportingStore.fetchAttributeValuesForSelectedScenarios();
    },

    // Returns the tree row text
    getName(entry) {
      return entry.name || entry.description;
    },

    // Build tree rows for AG-grid respecting the hierarchy level
    getHierarchyData(entriesInLevel, hierarchyPick, nameFn = this.getName, hierarchyId = null) {
      return entriesInLevel.map(entry => {
        const hierarchy = values(pick(entry, hierarchyPick));

        return {
          hierarchy: hierarchy.concat(entry[hierarchyId] || entry._id),
          name: nameFn(entry),
          // helper properties
          ...pick(entry, [
            '_id',
            'menuLevel',
            'scenarioId',
            'storeClassId',
            'clusterId',
            'canvasIdCombined',
            'totalCanvases',
            'totalExisting',
            'totalForecasted',
            'isClustered',
            'hasBeenForecast',
            'live',
            'isFinalCheckpoint',
          ]),
        };
      });
    },

    async resetData() {
      this.rowData = [];

      let scenarioData = [];
      const bundles = [];

      const bundleWorkpackages = flatMap(this.workpackageScenarioSelectStore.selectedBundles, b =>
        get(this.workpackagesStore.workpackagesByBundle, b._id, [])
      );

      const wps = uniqBy(
        [
          ...this.workpackageScenarioSelectStore.selectedWorkpackages,
          ...(this.bundlesEnabled ? bundleWorkpackages : []),
        ],
        '_id'
      );

      const workpackageData = reduce(
        sortBy(wps, 'creationDate'),
        (acc, w) => {
          // Ensures that all workpackages not assigned to a bundle have the bundleId key
          if (this.bundlesEnabled && !w.bundleId) w.bundleId = null;
          // bundleIds are required so that scenarios appear in the correct position in the menu
          const wpScenarios = map(
            this.workpackageScenarioSelectStore.scenariosByWorkpackage[w._id],
            s => ({
              ...s,
              bundleId: w.bundleId,
            })
          );
          // Workpackage not included if it does not have scenarios
          if (!size(wpScenarios)) return acc;

          scenarioData = scenarioData.concat(wpScenarios);
          if (this.bundlesEnabled) {
            const bundle = this.bundlesStore.bundlesById[w.bundleId] || this.noBundle;
            bundles.push({ ...bundle, menuLevel: menuLevel.bundle });
          }
          w.menuLevel = menuLevel.workpackage;
          acc.push(w);
          return acc;
        },
        []
      );
      const bundleData = sortBy(uniqBy(bundles, '_id'), 'creationDate');

      // There is no data to display if there are no scenarios
      if (!size(scenarioData)) return;

      if (this.enableFinalCheckpoints) {
        // Get final scenarios for workpackages
        await this.assortmentCanvasStore.fetchFinalScenarios(map(workpackageData, '_id'));
      }

      const bundlePick = this.bundlesEnabled ? ['bundleId'] : [];
      const bundleLevel = this.bundlesEnabled ? this.getHierarchyData(bundleData) : [];
      const workpackageLevel = this.getHierarchyData(workpackageData, bundlePick);
      const scenarioLevel = this.getHierarchyData(scenarioData, [...bundlePick, 'workpackageId']);
      const canvasesLevel = this.getHierarchyData(
        this.reportingStore.canvases,
        [...bundlePick, 'workpackageId', 'scenarioId'],
        this.reportingStore.getCanvasName,
        'canvasIdCombined'
      );
      const checkpointsLevel = this.getHierarchyData(
        this.reportingStore.checkpoints,
        [...bundlePick, 'workpackageId', 'scenarioId', 'canvasIdCombined'],
        this.getCheckpointName
      );
      this.rowData = this.rowData.concat(
        bundleLevel,
        workpackageLevel,
        scenarioLevel,
        canvasesLevel,
        checkpointsLevel
      );

      this.reportingStore.fetchAttributeValuesForSelectedScenarios();
    },

    // toggle which rows can be selected or not as baseline and comparisons change
    isComparisonEnabled(params) {
      return params.field === reportingFilters.comparison2
        ? size(this.reportingStore.baselineIds) && size(this.reportingStore.comparisonIds)
        : size(this.reportingStore.baselineIds);
    },

    // include new query to url param
    navigateToNewUrl(params) {
      const currentQueryValues = this.route.query || {};
      let newQueryValues = {
        ...currentQueryValues,
        ...params,
      };

      each(reportingFilters, filter => {
        // Ensure baseline and comparisons are always arrays
        newQueryValues[filter] = concat([], newQueryValues[filter] || []);
        if (!size(newQueryValues[filter])) delete newQueryValues[filter];
      });

      if (!newQueryValues.comparison || !newQueryValues.baseline) {
        newQueryValues = omit(newQueryValues, ['comparison', 'comparison2']);
      }
      if (!newQueryValues.comparison2) delete newQueryValues.comparison2;

      // Ensure workpackage and bundleIds are always arrays
      if (newQueryValues.workpackageIds) {
        newQueryValues.workpackageIds = concat([], newQueryValues.workpackageIds || []);
      }
      if (this.bundlesEnabled && newQueryValues.bundleIds) {
        newQueryValues.bundleIds = concat([], newQueryValues.bundleIds || []);
      }

      this.router.push({
        path: this.route.path, // keep on same path
        query: newQueryValues,
      });
    },

    getCheckpointName(entry) {
      return get(entry, 'checkpointMeta.name', entry.description);
    },

    onGridReady(params) {
      this.gridApi = params.api;
    },

    onExpand() {
      this.gridApi.sizeColumnsToFit();
      this.gridApi.expandAll();
    },

    isRowHighlighted(params) {
      const allChildrenCount = get(params, 'node.allChildrenCount');
      const allLeafChildren = get(params, 'node.allLeafChildren', []);
      const allLeafChildrenIds = map(allLeafChildren, 'data._id');

      const baselineIds = new Set(this.reportingStore.baselineIds);
      const comparisonIds = new Set(this.reportingStore.comparisonIds);
      const comparison2Ids = new Set(this.reportingStore.comparison2Ids);

      if (size(allLeafChildrenIds) && allChildrenCount) {
        return some(
          allLeafChildrenIds,
          id => baselineIds.has(id) || comparisonIds.has(id) || comparison2Ids.has(id)
        );
      }
      const id = params.data._id;
      if (!id) return false;
      return baselineIds.has(id) || comparisonIds.has(id) || comparison2Ids.has(id);
    },

    getNextFilters({ data, field }) {
      const stateValue = data._id;
      const propertyId = `${field}Ids`;
      const fieldIds = new Set(this.reportingStore[propertyId]);
      if (fieldIds.has(stateValue)) fieldIds.delete(stateValue);
      else fieldIds.add(stateValue);
      const nextSelectionValues = [...fieldIds];

      const params = reduce(
        values(reportingFilters),
        (acc, filter) => {
          const key = `${filter}Ids`;
          return {
            ...acc,
            [key]: key === propertyId ? nextSelectionValues : this.reportingStore[key],
          };
        },
        {}
      );
      const attributeValue = this.reportingStore.getFirstCommonAttribute(params);

      return {
        attributeValue,
        [field]: nextSelectionValues,
      };
    },

    async onGenerateReports() {
      // this method is called when the user clicks on the "Generate" button
      this.reportingStore.triggerReportUpdate();
      // collapse the sidebar after generating reports
      this.reportingStore.isSidebarShown = false;
      this.reportingStore.setReportDataStale(false);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_all.scss';

$reporting-menu-bottom-padding: 18px;

.bg-reporting-menu {
  background-color: $reporting-section-bg-color;
}

.grid-container {
  height: calc(100% - $assortment-footer-height - $reporting-menu-bottom-padding);
}

:deep(.round-selection) {
  color: $baseline-colour;
  font-family: 'Material Design Icons';
  font-size: 15px;
  position: relative;

  &:before {
    content: '\F043D';
    height: 15px;
    width: 15px;
  }

  &[disabled]:before {
    color: $assortment-disabled-text-button-colour;
  }
}

:deep(.selected-baseline):before,
:deep(.selected-comparison):before,
:deep(.selected-comparison2):before {
  content: '\F043E';
  height: 15px;
  width: 15px;
}

:deep(.selected-baseline) {
  color: $baseline-colour;
}

:deep(.selected-comparison) {
  color: $comparison-colour;
}

:deep(.selected-comparison2) {
  color: $comparison2-colour;
}

:deep(.ag-popup > .ag-tooltip) {
  color: $assortment-text-colour !important;
  background-color: $assortment-dialog-background !important;
  border: 1px solid $assortment-text-colour !important;
  min-width: fit-content !important;
}

:deep(.unforecasted-checkpoint) {
  color: $assortment-disabled-text-button-colour;
}

:deep(.forecasted) {
  color: $success-colour;
}

:deep(.notForecasted) {
  color: $reporting-disabled-icon-color;
}

:deep(.ag-layout-normal > .ag-body-viewport.ag-layout-normal) {
  scrollbar-gutter: stable;
}
</style>
